import React from 'react';
import {
  Box,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

type CodeRow = { code: string; manufacturer: string; model: string; engine: string };

const concernExamples: CodeRow[] = [
  { code: 'VWUIDBU1', manufacturer: 'Volkswagen užitkové vozy', model: 'ID.Buzz', engine: 'Elektromotor ()' },
  { code: 'VWUIDBU1', manufacturer: 'Volkswagen užitkové vozy', model: 'ID.Buzz', engine: '0-999 ccm ()' },
  { code: 'AUDQ7XX5', manufacturer: 'Audi', model: 'SQ7', engine: '2500 ccm a více ()' },
  { code: 'AUDQ5XX5', manufacturer: 'Audi', model: 'SQ5', engine: '2500 ccm a více ()' },
  { code: 'AUDA7XX5', manufacturer: 'Audi', model: 'S7', engine: '2500 ccm a více ()' },
  { code: 'AUDQ3XX5', manufacturer: 'Audi', model: 'RSQ3', engine: '2500 ccm a více ()' },
  { code: 'AUDQ3XX4', manufacturer: 'Audi', model: 'RSQ3', engine: '1850-2499 ccm ()' },
  { code: 'AUDA3XX4', manufacturer: 'Audi', model: 'RS3', engine: '1850-2499 ccm ()' },
  { code: 'AUDA3XX5', manufacturer: 'Audi', model: 'RS3', engine: '2500 ccm a více ()' },
  { code: 'AUDA5XX5', manufacturer: 'Audi', model: 'RS5', engine: '2500 ccm a více ()' },
  { code: 'AUDA4XX5', manufacturer: 'Audi', model: 'RS4', engine: '2500 ccm a více ()' },
  { code: 'AUDA6XX5', manufacturer: 'Audi', model: 'RS6', engine: '2500 ccm a více ()' },
];

const nonConcernHyundai: CodeRow[] = [
  { code: 'HYUXXXX1', manufacturer: 'Hyundai', model: 'Hyundai', engine: '0-999 ccm ()' },
  { code: 'HYUXXXX2', manufacturer: 'Hyundai', model: 'Hyundai', engine: '1000-1349 ccm ()' },
  { code: 'HYUXXXX3', manufacturer: 'Hyundai', model: 'Hyundai', engine: '1350-1849 ccm ()' },
  { code: 'HYUXXXX4', manufacturer: 'Hyundai', model: 'Hyundai', engine: '1850-2499 ccm ()' },
  { code: 'HYUXXXX5', manufacturer: 'Hyundai', model: 'Hyundai', engine: '2500 ccm a více ()' },
];

function CodesTable({ rows }: { rows: CodeRow[] }) {
  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table
        size='small'
        sx={{ '& th': { fontWeight: 'bold' } }}
      >
        <TableHead>
          <TableRow>
            <TableCell>MBV kód</TableCell>
            <TableCell>Výrobce</TableCell>
            <TableCell>Model</TableCell>
            <TableCell>Motor (kód)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.code}-${i}`}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{r.code}</TableCell>
              <TableCell>{r.manufacturer}</TableCell>
              <TableCell>{r.model}</TableCell>
              <TableCell>{r.engine}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ComponentDefinition({
  label,
  rule,
  example,
}: {
  label: string;
  rule: string;
  example: string;
}) {
  return (
    <Box
      sx={{
        borderLeft: 3,
        borderColor: 'primary.light',
        pl: 2,
        py: 0.5,
        mb: 1.5,
      }}
    >
      <Typography
        variant='overline'
        sx={{ fontWeight: 'bold', lineHeight: 1.2, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant='body2'>{rule}</Typography>
      <Typography
        variant='body2'
        sx={{ fontFamily: 'monospace', color: 'text.secondary', mt: 0.5 }}
      >
        {example}
      </Typography>
    </Box>
  );
}

export default function ModelKeyInfoContent() {
  return (
    <Box>
      {/* Section: Modelový klíč vozidla */}
      <Typography
        variant='subtitle1'
        fontWeight='bold'
        gutterBottom
      >
        Modelový klíč vozidla
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Jednoznačný identifikátor konkrétního modelu vozidla až na úroveň stupně výbavy a konkrétní výbavy (konkrétního
        provedení automobilu).
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Section: Z čeho se skládá MBV kód? */}
      <Typography
        variant='h6'
        fontStyle='italic'
        gutterBottom
      >
        Z čeho se skládá MBV kód?
      </Typography>

      <Typography
        variant='subtitle2'
        fontWeight='bold'
        fontStyle='italic'
        gutterBottom
        sx={{ mt: 1.5 }}
      >
        Jaký je rozdíl MBV kódu a VIN kódu?
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        VIN kód existuje pouze u již vyrobených vozidel a jednoznačně identifikuje konkrétní vozidlo unikátně. MBV kód
        je stejný pro stejně provedená auta se stejnou výbavou.
      </Typography>

      <Typography
        variant='subtitle2'
        fontWeight='bold'
        fontStyle='italic'
        gutterBottom
        sx={{ mt: 1.5 }}
      >
        Struktura MBV kódu
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Modelové klíče vytváří/generuje Škoda Auto a nelze z jejich zápisu žádným způsobem určit, ke kterému modelu a
        výbavě se vztahují. Jedná se o sérii znaků s určitou podobností, ale neznáme logiku, jak jsou sestavovány.
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        {' '}Modelové klíče mohou být rozšířeny o tzv. extenze (další řetězec za lomítkem), které rozšiřují a více
        specifikují daný model a stupeň výbavy (např. odlišení navi, infortainmentu atp.). Ne vždy jsou extenze
        relevantní.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Section: Fiktivní modelové klíče */}
      <Typography
        variant='h6'
        fontStyle='italic'
        gutterBottom
      >
        Fiktivní modelové klíče
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Ve VWFS byly zavedeny <strong>Fake MBV kódy </strong>(jiné názvy -<strong> obecné modelové klíče</strong>,{' '}
        <strong>fiktivní modelové klíče</strong>),{' '}které abstrahují zápis na úroveň značky, modelu a rozsahu
        obsahu motoru (pásma rozsahů pro obsah motoru v CM3).
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Fiktivní modelové klíče byly zavedeny s účelem využití Kalkulačky financování tam, kde není známý reálný
        modelový kód (odběratel Kalkulačky nemá tyto informace k dispozici, ale chce Kalkulačku používat na svém Webu)
      </Typography>
      <Typography
        variant='body2'
        sx={{ mb: 2 }}
      >
        Fiktivní MBV kód lze odvodit dle jmenné konvence: [značka][model][obsah motoru]
      </Typography>

      <ComponentDefinition
        label='ZNAČKA'
        rule='3 znaky'
        example='ŠKO, VWU, POR, SEA, AUD…'
      />

      <ComponentDefinition
        label='MODEL'
        rule='4 znaky, pokud nejsou všechny využity, doplňují se písmenem X'
        example='SCAL, A6XX…'
      />

      <Box
        sx={{
          borderLeft: 3,
          borderColor: 'primary.light',
          pl: 2,
          py: 0.5,
          mb: 2,
        }}
      >
        <Typography
          variant='overline'
          sx={{ fontWeight: 'bold', lineHeight: 1.2, display: 'block' }}
        >
          OBSAH MOTORU
        </Typography>
        <Typography variant='body2'>1 číslice v rozsahu hodnot 1-5</Typography>
        <Typography variant='body2'>Číslo reprezentuje pořadí v rozsahu hodnot pro obsah motoru</Typography>
        <Box
          sx={{
            mt: 0.75,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            fontFamily: 'monospace',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          <span>1:</span>
          <span>0 - 999ccm</span>
          <span>2:</span>
          <span>1000 - 1349 ccm</span>
          <span>3:</span>
          <span>1350 - 1849 ccm</span>
          <span>4:</span>
          <span>1850 - 2499 ccm</span>
          <span>5:</span>
          <span>2500 ccm a více</span>
        </Box>
      </Box>

      <Typography
        variant='body2'
        paragraph
      >
        Přehled všech existujících modelových klíčů, a zavedení dalších (v případě výroby nového modelu) lze provést
        zde:{' '}
        <Link
          href='https://editng.vwfs.cz/admin/Calculator'
          target='_blank'
          rel='noopener noreferrer'
        >
          Správa Kalkulačky - generování MBV klíčů
        </Link>
        {' '}(přístup jen oprávněným uživatelům)
      </Typography>

      <Typography
        variant='body2'
        sx={{ fontWeight: 'medium', mt: 2, mb: 1 }}
      >
        Příklady fiktivních MBV kódů pro koncernové značky:
      </Typography>
      <Box sx={{ mb: 2 }}>
        <CodesTable rows={concernExamples} />
      </Box>

      <Typography variant='body2'>Fiktivní MBV kódy používáme i pro nekoncernové značky.</Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Pro každou nekoncernovou značku existuje 5 fiktivních MBV kódů. A to podle rozsahu kubatury.
      </Typography>
      <Typography
        variant='body2'
        sx={{ mb: 1 }}
      >
        Na obrázku níže jsou uvedeny kódy pro Hyndai. Podobným způsobem se generují kódy i pro ostatní nekoncernové
        značky ([značka]XXXX[1..5, dle obsahu motoru])
      </Typography>
      <Box sx={{ mb: 2 }}>
        <CodesTable rows={nonConcernHyundai} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Section: Práce s MBV kódem v Kalkulačkách */}
      <Typography
        variant='subtitle1'
        fontWeight='bold'
        gutterBottom
      >
        Práce s MBV kódem v Kalkulačkách
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Při volání služby WebCalc (FD Proxy) MBV kód je jedním z hlavních parametrů, který vstupuje do procesu
        kalkulace.
      </Typography>
      <Typography
        variant='body2'
        paragraph
      >
        Dle MBV kódu (a domény) se dohledává nastavení parametrizace produktu (v ProFIN+), pro který Kalkulačka má
        vypočítat splátku.
      </Typography>
      <Typography variant='body2'>
        V ProFIN+ je dále nastavená logika, jakým způsobem se má daný MBV kód dohledat (určitý algoritmus dohledání -
        zda se bude hledat přesná shoda celého zaslaného MBV klíče, anebo se budou při hledání ignorovat některé znaky
        (velmi zjednodušeně řečeno, celý algoritmus je složitý a jeho správa je v kompetenci Parametrizátorů)
      </Typography>
    </Box>
  );
}
